import type { SendbirdChatSDK } from '@sendbird/uikit-utils';
import { useSetAtom } from 'jotai/index';
import * as React from 'react';
import { useMemo, useState } from 'react';
import { Image } from 'react-native';
import Animated, { FadeIn, FadeInDown } from 'react-native-reanimated';
import {
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    H5Emphasized,
    Spinner,
    View,
    YStack,
} from 'smoothie';

import TasksComplete from '../../../../assets/images/goals/tasks_complete.png';
import { StorageKeys } from '../../../common/constants';
import { formatDateWithOrdinalSuffix } from '../../../common/misc';
import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { useStorage } from '../../../common/providers/StorageProvider';
import useGuideMessaging from '../../../guideMessaging/hooks/useGuideMessaging';
import useSendbird from '../../../guideMessaging/hooks/useSendbird';
import type { GuideMessagingDto } from '../../../guideMessaging/types/GuideMessaging';
import { captureException } from '../../../logger/Logger';
import { fireGoalConfettiCannonAtom } from '../../state/GoalAtoms';
import type { IGoal } from '../../types/Goal';

type GoalCompleteNotifications = Record<string, string>;

interface HasNotifiedGuideProps {
    guideFirstName?: string | null;
    completedDate?: string | null;
}

const HasNotifiedGuide = ({
    guideFirstName,
    completedDate,
}: HasNotifiedGuideProps) => {
    const formattedCompletedDate = useMemo(() => {
        const date =
            completedDate != null ? new Date(completedDate) : new Date();

        return formatDateWithOrdinalSuffix(date, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    }, [completedDate]);

    return (
        <Animated.View entering={FadeIn} style={{ width: '100%' }}>
            <View
                width="100%"
                gap={8}
                padding={8}
                borderRadius={12}
                borderWidth={1}
                borderColor="$gray200"
            >
                <BodySmallRegular color="$gray1200">
                    You completed these tasks and notified {guideFirstName}{' '}
                    {completedDate != null
                        ? `on ${formattedCompletedDate}`
                        : ''}
                </BodySmallRegular>
            </View>
        </Animated.View>
    );
};

interface NotifyGuideProps {
    goal: IGoal;
}

const NotifyGuide = ({ goal }: NotifyGuideProps) => {
    const storage = useStorage();
    const goalCompleteNotifications = JSON.parse(
        storage.getString(StorageKeys.GOAL_COMPLETE_NOTIFICATIONS) ?? '{}',
    ) as GoalCompleteNotifications;

    const [isSendingMessage, setIsSendingMessage] = useState<boolean>(false);
    const [hasNotifiedGuideDate, setHasNotifiedGuideDate] = useState<
        string | null
    >(goalCompleteNotifications[goal.id] ?? null);

    const user = useAuthedUser();
    const { guideMessaging } = useGuideMessaging();
    const { sdk: sendbirdSdk } = useSendbird();

    const { firstName: guideFirstName } = user.membership?.guide ?? {};

    const setFireGoalConfettiCannon = useSetAtom(fireGoalConfettiCannonAtom);

    const sendGoalCompleteMessage = async () => {
        try {
            setFireGoalConfettiCannon(true);
            setIsSendingMessage(true);

            if (
                (guideMessaging as GuideMessagingDto).currentChat.channel
                    .channelUrl != null
            ) {
                const completedDate = new Date();

                const channel = await (
                    sendbirdSdk as SendbirdChatSDK
                ).groupChannel.getChannel(
                    (guideMessaging as GuideMessagingDto).currentChat.channel
                        .channelUrl,
                );

                channel.sendUserMessage({
                    message: `Hey ${guideFirstName}, I've completed all the tasks for my goal: "${goal.description}"`,
                });
                storage.setString(
                    StorageKeys.GOAL_COMPLETE_NOTIFICATIONS,
                    JSON.stringify({
                        ...goalCompleteNotifications,
                        [goal.id]: completedDate,
                    }),
                );
                setHasNotifiedGuideDate(completedDate.toDateString());
            }
        } catch (e) {
            captureException(e);
        } finally {
            setIsSendingMessage(false);
        }
    };

    if (isSendingMessage) return <Spinner height={80} />;

    return (
        <YStack
            flex={1}
            gap={20}
            width="100%"
            maxWidth={400}
            alignItems="center"
            justifyContent="center"
        >
            {hasNotifiedGuideDate != null ? (
                <HasNotifiedGuide
                    guideFirstName={guideFirstName}
                    completedDate={hasNotifiedGuideDate}
                />
            ) : (
                <View flex={1} gap={20} width="100%" alignItems="center">
                    <Animated.View entering={FadeInDown}>
                        <Image
                            style={{
                                width: 182,
                                height: 182,
                            }}
                            source={TasksComplete}
                        />
                    </Animated.View>
                    <Animated.View
                        entering={FadeInDown.delay(100)}
                        style={{ width: 245 }}
                    >
                        <H5Emphasized textAlign="center">
                            All your tasks have been completed!
                        </H5Emphasized>
                    </Animated.View>
                    {sendbirdSdk && guideMessaging && (
                        <Animated.View
                            entering={FadeIn}
                            style={{ width: '100%' }}
                        >
                            <Button
                                variant={ButtonVariant.SECONDARY}
                                tone="positive"
                                size={ButtonSize.LARGE}
                                text="Notify Guide"
                                onPress={sendGoalCompleteMessage}
                            />
                        </Animated.View>
                    )}
                </View>
            )}
        </YStack>
    );
};

export default NotifyGuide;
